import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  HelpCircle,
  ChevronDown,
  Search,
  Sparkles,
  Calculator,
  ShieldCheck,
  BatteryCharging,
  Wrench,
  PhoneCall,
  MessageSquare,
  Zap,
  CheckCircle2,
  ArrowRight,
  Sun,
  X,
  Filter,
  Check
} from 'lucide-react';
import { useSolarStore } from '../../store/useSolarStore';

export interface FAQItem {
  id: string;
  category: 'INSTALLATION' | 'ROI_FINANCIAL' | 'BATTERY_STORAGE' | 'MAINTENANCE_WARRANTY';
  categoryLabel: string;
  question: string;
  answer: string;
  tags: string[];
}

export const FAQ_DATA: FAQItem[] = [
  // CATEGORY 1: INSTALLATION & SIZING
  {
    id: 'faq-1',
    category: 'INSTALLATION',
    categoryLabel: 'Installation & Sizing',
    question: 'How do I know what kVA inverter size my home or office needs?',
    answer:
      'Sizing depends on your concurrent electrical load (wattage of appliances running simultaneously). A 1.5kVA system powers basic lighting, TV, fans, and laptops. A 3.5kVA supports a refrigerator and light loads. A 5kVA or 7.5kVA handles multiple inverter air conditioners (1HP–1.5HP), deep freezers, and water pumps. Our Live Dynamic Sizing Tool on this page calculates your exact requirements based on your appliance checklist.',
    tags: ['kVA', 'Sizing', 'Load', 'Inverter', 'Capacity'],
  },
  {
    id: 'faq-2',
    category: 'INSTALLATION',
    categoryLabel: 'Installation & Sizing',
    question: 'How long does a typical solar installation take from order to commission?',
    answer:
      'Standard residential installations (1.5kVA to 10kVA) are completed within 24 to 48 hours after site survey clearance. Commercial 15kVA+ microgrids usually take 3 to 5 business days, including AC/DC cable routing, surge protection setup, DB board separation, and cloud monitoring activation.',
    tags: ['Timeline', 'Installation', 'Speed', 'Commissioning'],
  },
  {
    id: 'faq-3',
    category: 'INSTALLATION',
    categoryLabel: 'Installation & Sizing',
    question: 'Can solar panels be installed on any type of roof in Nigeria?',
    answer:
      'Yes. We install on aluminium longspan sheets, step-tiles, Gerard roofs, stone-coated tiles, and flat concrete slabs. We utilize custom anodized aluminium mounting rails and stainless-steel roof hooks/brackets with weatherproof rubber sealant gaskets to guarantee 100% leak-proof roof integrity.',
    tags: ['Roof', 'Mounting', 'Leak-proof', 'Aluminium', 'Tile'],
  },
  {
    id: 'faq-4',
    category: 'INSTALLATION',
    categoryLabel: 'Installation & Sizing',
    question: 'Do I need to re-wire my house distribution board before installing solar?',
    answer:
      'No full rewiring is needed. However, our certified COREN engineers perform a "load separation" at your Distribution Board (DB). Heavy non-essential loads (e.g., central electric water heaters, heavy electric stoves) are isolated so they don’t accidentally drain or overload your solar inverter backup circuit.',
    tags: ['Wiring', 'Distribution Board', 'Load Separation', 'DB Box'],
  },
  {
    id: 'faq-5',
    category: 'INSTALLATION',
    categoryLabel: 'Installation & Sizing',
    question: 'What happens if my roof has shade from trees or nearby buildings?',
    answer:
      'We conduct a shade assessment during site auditing using solar pathfinders. If shading exists, we configure panel arrays into separate MPPT string trackers or utilize half-cut PERC/TOPCon solar cell panels that maintain high power harvest even when partial shade falls on one section of the array.',
    tags: ['Shade', 'MPPT', 'TOPCon', 'Performance', 'Trees'],
  },

  // CATEGORY 2: ROI & FINANCIAL SAVINGS
  {
    id: 'faq-6',
    category: 'ROI_FINANCIAL',
    categoryLabel: 'ROI & Fuel Savings',
    question: 'How quickly will I recover my initial solar investment in Nigeria?',
    answer:
      'With Band A grid tariffs at ₦225/kWh and petrol/diesel prices fluctuating between ₦1,000–₦1,300/liter, most residential and commercial solar owners reach complete financial payback (ROI) within 10 to 14 months. Every month after that represents pure free electricity and direct cash retention.',
    tags: ['ROI', 'Payback', 'Band A', 'Tariff', 'Savings'],
  },
  {
    id: 'faq-7',
    category: 'ROI_FINANCIAL',
    categoryLabel: 'ROI & Fuel Savings',
    question: 'Is solar really cheaper than running a 3.5kVA to 10kVA fuel generator?',
    answer:
      'Significantly cheaper. Running a 5kVA generator for 10 hours daily consumes roughly 15 liters of petrol (~₦16,500/day or ₦495,000/month). Over 2 years, generator fuel and servicing cost over ₦11.8 Million with zero asset value. A solar system provides 24/7 quiet power for 15+ years with zero fuel costs.',
    tags: ['Generator', 'Petrol', 'Diesel', 'Comparison', 'Cost'],
  },
  {
    id: 'faq-8',
    category: 'ROI_FINANCIAL',
    categoryLabel: 'ROI & Fuel Savings',
    question: 'Do you offer flexible BNPL or monthly installment financing options?',
    answer:
      'Yes! Through our partner financial institutions and Access Tech Solar Financing program, qualified residential and corporate clients can switch to solar with an initial 20–30% down payment and spread the balance over 6 to 24 flexible monthly installments.',
    tags: ['Financing', 'BNPL', 'Installments', 'Payment Plan'],
  },
  {
    id: 'faq-9',
    category: 'ROI_FINANCIAL',
    categoryLabel: 'ROI & Fuel Savings',
    question: 'Will installing solar increase the market value of my property?',
    answer:
      'Absolutely. Properties in Nigeria equipped with 24/7 clean solar backup command 20–35% higher rental yields and sell up to 50% faster in the real estate market because tenants and buyers avoid the hassle and noise of generators and grid blackouts.',
    tags: ['Property Value', 'Rent', 'Real Estate', 'Yield'],
  },
  {
    id: 'faq-10',
    category: 'ROI_FINANCIAL',
    categoryLabel: 'ROI & Fuel Savings',
    question: 'Are there any hidden recurring costs after solar installation?',
    answer:
      'No hidden fees. Unlike fuel generators that require frequent oil changes, spark plugs, and mechanical overhauls every few weeks, a solar system has zero moving parts. The only routine task is periodically washing dust off solar panels with clean water.',
    tags: ['Hidden Fees', 'Maintenance Cost', 'Servicing'],
  },

  // CATEGORY 3: BATTERIES & STORAGE
  {
    id: 'faq-11',
    category: 'BATTERY_STORAGE',
    categoryLabel: 'Batteries & Storage',
    question: 'Why do you use LiFePO4 Lithium Iron Phosphate batteries instead of tubular gel/lead-acid?',
    answer:
      'LiFePO4 (Lithium Iron Phosphate) is superior in every metric. They last 6,000+ deep discharge cycles (12–15+ years) compared to tubular gel batteries which degrade in 2–3 years. Lithium batteries offer 95% usable Depth of Discharge (DoD), charge 3x faster, weigh 70% less, and do not emit toxic gases.',
    tags: ['LiFePO4', 'Lithium', 'Gel', 'Tubular', 'Battery Life'],
  },
  {
    id: 'faq-12',
    category: 'BATTERY_STORAGE',
    categoryLabel: 'Batteries & Storage',
    question: 'How many hours of backup will the battery supply during prolonged grid blackouts?',
    answer:
      'Backup duration depends on battery kWh capacity and connected load. For example, a 5kWh lithium battery running a 500W load (lights, TV, fridge, fans, laptops) provides 10 consecutive hours of backup. During daylight hours, solar panels power the load directly while simultaneously charging the battery.',
    tags: ['Backup Hours', 'Runtime', 'Blackout', 'Grid Outage'],
  },
  {
    id: 'faq-13',
    category: 'BATTERY_STORAGE',
    categoryLabel: 'Batteries & Storage',
    question: 'Can I expand my solar panel array or battery storage capacity in the future?',
    answer:
      'Yes! All Access Tech hybrid inverters and modular lithium battery racks feature scalable parallel expansion. You can start with a 3.5kVA system with 5kWh lithium storage today and add more solar panels or battery modules seamlessly as your family or business grows.',
    tags: ['Expansion', 'Upgrade', 'Scalable', 'Modular'],
  },
  {
    id: 'faq-14',
    category: 'BATTERY_STORAGE',
    categoryLabel: 'Batteries & Storage',
    question: 'What is the typical lifespan of solar panels and hybrid inverters?',
    answer:
      'Our Tier-1 Monocrystalline N-Type TOPCon panels come with a 25-Year Linear Power Performance Warranty (guaranteeing >80% output after 25 years). Hybrid pure sine inverters and LiFePO4 lithium batteries are rated for 10 to 15+ years of reliable operation.',
    tags: ['Lifespan', 'Warranty', 'TOPCon', 'Inverter Life'],
  },
  {
    id: 'faq-15',
    category: 'BATTERY_STORAGE',
    categoryLabel: 'Batteries & Storage',
    question: 'Does cloudy weather or rain in Nigeria stop solar panels from producing power?',
    answer:
      'No. Solar PV panels convert light energy (photons), not heat. Even on overcast, cloudy, or rainy days in Lagos, Abuja, or Port Harcourt, modern N-Type solar panels harvest 25% to 45% of their peak power. Furthermore, your lithium battery bank covers any daytime gap seamlessly.',
    tags: ['Rain', 'Clouds', 'Weather', 'Photons', 'Winter'],
  },

  // CATEGORY 4: MAINTENANCE, WARRANTY & SAFETY
  {
    id: 'faq-16',
    category: 'MAINTENANCE_WARRANTY',
    categoryLabel: 'Maintenance & Warranty',
    question: 'What warranty protection comes with an Access Tech solar installation?',
    answer:
      'Every complete installation includes comprehensive warranties: 25-Year Performance Warranty on Solar Panels, 5-Year Full Replacement Warranty on LiFePO4 Lithium Batteries, 3-Year Factory Warranty on Hybrid Inverters, and 1-Year Workmanship & Service Guarantee by our certified installers.',
    tags: ['Warranty', 'Guarantee', 'Protection', 'Service'],
  },
  {
    id: 'faq-17',
    category: 'MAINTENANCE_WARRANTY',
    categoryLabel: 'Maintenance & Warranty',
    question: 'How do I monitor my system’s live performance and battery level?',
    answer:
      'All our hybrid inverters come equipped with built-in Wi-Fi / 4G IoT smart dongles. You can track real-time solar generation, grid status, battery State-of-Charge (SOC), and power consumption live from your iOS/Android smartphone or desktop web portal anywhere in the world.',
    tags: ['Monitoring', 'App', 'IoT', 'Wi-Fi', 'Telemetry'],
  },
  {
    id: 'faq-18',
    category: 'MAINTENANCE_WARRANTY',
    categoryLabel: 'Maintenance & Warranty',
    question: 'Is solar power safe during lightning storms or heavy rainfall?',
    answer:
      'Yes. Every system we install incorporates industrial-grade DC/AC Surge Protection Devices (SPD), Type 2 Lightning Arrestors, Circuit Breakers, and dedicated Copper Earth Rod grounding. This insulates your home electronics and solar equipment completely from voltage spikes and lightning strikes.',
    tags: ['Lightning', 'Surge Protection', 'Safety', 'Grounding'],
  },
  {
    id: 'faq-19',
    category: 'MAINTENANCE_WARRANTY',
    categoryLabel: 'Maintenance & Warranty',
    question: 'What routine maintenance is required for a residential solar system?',
    answer:
      'Maintenance is minimal. We recommend wiping dust, leaves, or soot off the solar panel glass surfaces with clean water and a soft mop every 2 to 3 months (especially during Harmattan). Our team also offers optional semi-annual health check-up visits.',
    tags: ['Cleaning', 'Harmattan', 'Routine', 'Servicing'],
  },
  {
    id: 'faq-20',
    category: 'MAINTENANCE_WARRANTY',
    categoryLabel: 'Maintenance & Warranty',
    question: 'What happens if my inverter experiences a fault or system error?',
    answer:
      'Our 24/7 Smart Telemetry Monitoring System alerts our technical response team instantly if an anomaly occurs. In the rare event of a hardware issue, our local Nigerian service centers dispatch a certified engineer within 12–24 hours or provide remote OTA firmware diagnostics.',
    tags: ['Fault', 'Support', 'Engineers', 'Service Center'],
  },
];

export const FAQSection: React.FC = () => {
  const { setIsLeadModalOpen } = useSolarStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [openFaqIds, setOpenFaqIds] = useState<Set<string>>(new Set(['faq-1', 'faq-6']));

  // Filter FAQs based on Search and Selected Category
  const filteredFaqs = useMemo(() => {
    return FAQ_DATA.filter((faq) => {
      const matchesCategory = selectedCategory === 'ALL' || faq.category === selectedCategory;
      const query = searchQuery.toLowerCase().trim();
      if (!query) return matchesCategory;

      const matchesQuery =
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query) ||
        faq.tags.some((tag) => tag.toLowerCase().includes(query));

      return matchesCategory && matchesQuery;
    });
  }, [searchQuery, selectedCategory]);

  const toggleFaq = (id: string) => {
    setOpenFaqIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const expandAll = () => {
    setOpenFaqIds(new Set(filteredFaqs.map((f) => f.id)));
  };

  const collapseAll = () => {
    setOpenFaqIds(new Set());
  };

  return (
    <section className="relative py-12 sm:py-16 bg-slate-950 border-t border-slate-900 text-white overflow-hidden">
      {/* Background Glow Accents */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-emerald-500/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-cyan-500/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="w-full max-w-none space-y-10 relative z-10">
        {/* Section Title & Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold uppercase tracking-wider">
            <HelpCircle className="w-4 h-4 text-emerald-400" /> Essential Knowledge Base
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-white tracking-tight">
            Frequently Asked Questions
          </h2>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
            Everything you need to know about switching to solar power in Nigeria — from sizing and financial payback to lithium battery lifespan and COREN installation standards.
          </p>
        </div>

        {/* Search Bar & Category Filters */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-4 sm:p-6 shadow-xl space-y-5">
          <div className="flex flex-col md:flex-row items-center gap-4 justify-between">
            {/* Search Input */}
            <div className="relative w-full md:max-w-md">
              <Search className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search questions (e.g. Band A, LiFePO4, ROI, Warranty)..."
                className="w-full bg-slate-950 border border-slate-800 rounded-2xl pl-12 pr-10 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all font-medium"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-white rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Expand / Collapse Controls */}
            <div className="flex items-center gap-2 text-xs font-semibold shrink-0 self-end md:self-auto">
              <button
                onClick={expandAll}
                className="px-3.5 py-2 rounded-xl bg-slate-950 hover:bg-slate-800 border border-slate-800 text-slate-300 transition-all"
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="px-3.5 py-2 rounded-xl bg-slate-950 hover:bg-slate-800 border border-slate-800 text-slate-400 transition-all"
              >
                Collapse All
              </button>
            </div>
          </div>

          {/* Category Pills Filter */}
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-800/80">
            <span className="text-xs text-slate-400 font-bold uppercase tracking-wider mr-2 hidden sm:inline-flex items-center gap-1">
              <Filter className="w-3.5 h-3.5 text-emerald-400" /> Categories:
            </span>
            <button
              onClick={() => setSelectedCategory('ALL')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                selectedCategory === 'ALL'
                  ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/20'
                  : 'bg-slate-950 text-slate-300 hover:bg-slate-800 border border-slate-800'
              }`}
            >
              All Questions ({FAQ_DATA.length})
            </button>
            <button
              onClick={() => setSelectedCategory('INSTALLATION')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                selectedCategory === 'INSTALLATION'
                  ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/20'
                  : 'bg-slate-950 text-slate-300 hover:bg-slate-800 border border-slate-800'
              }`}
            >
              <Wrench className="w-3.5 h-3.5" /> Installation & Sizing (5)
            </button>
            <button
              onClick={() => setSelectedCategory('ROI_FINANCIAL')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                selectedCategory === 'ROI_FINANCIAL'
                  ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/20'
                  : 'bg-slate-950 text-slate-300 hover:bg-slate-800 border border-slate-800'
              }`}
            >
              <Calculator className="w-3.5 h-3.5" /> ROI & Fuel Savings (5)
            </button>
            <button
              onClick={() => setSelectedCategory('BATTERY_STORAGE')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                selectedCategory === 'BATTERY_STORAGE'
                  ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/20'
                  : 'bg-slate-950 text-slate-300 hover:bg-slate-800 border border-slate-800'
              }`}
            >
              <BatteryCharging className="w-3.5 h-3.5" /> Batteries & Storage (5)
            </button>
            <button
              onClick={() => setSelectedCategory('MAINTENANCE_WARRANTY')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                selectedCategory === 'MAINTENANCE_WARRANTY'
                  ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/20'
                  : 'bg-slate-950 text-slate-300 hover:bg-slate-800 border border-slate-800'
              }`}
            >
              <ShieldCheck className="w-3.5 h-3.5" /> Maintenance & Warranty (5)
            </button>
          </div>
        </div>

        {/* FAQ Accordion List */}
        <div className="space-y-4">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12 bg-slate-900/60 border border-slate-800 rounded-3xl p-8 space-y-3">
              <HelpCircle className="w-12 h-12 text-slate-600 mx-auto" />
              <h3 className="text-lg font-bold text-white">No questions matched your search</h3>
              <p className="text-xs text-slate-400 max-w-md mx-auto">
                Try typing a different keyword or browse all categories above. You can also talk directly to our solar engineering consultants.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('ALL');
                }}
                className="mt-2 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-bold text-slate-200 transition-colors"
              >
                Reset Search Filters
              </button>
            </div>
          ) : (
            filteredFaqs.map((faq, index) => {
              const isOpen = openFaqIds.has(faq.id);

              return (
                <div
                  key={faq.id}
                  className={`bg-slate-900 border transition-all rounded-2xl overflow-hidden ${
                    isOpen
                      ? 'border-emerald-500/50 shadow-lg shadow-emerald-500/5 bg-slate-900'
                      : 'border-slate-800 hover:border-slate-700'
                  }`}
                >
                  <button
                    onClick={() => toggleFaq(faq.id)}
                    className="w-full text-left p-5 sm:p-6 flex items-start justify-between gap-4 focus:outline-none"
                  >
                    <div className="space-y-1 pr-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-slate-950 text-emerald-400 border border-slate-800 font-mono">
                          Q{index + 1} • {faq.categoryLabel}
                        </span>
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-white pt-1 leading-snug">
                        {faq.question}
                      </h3>
                    </div>

                    <div
                      className={`w-8 h-8 rounded-xl shrink-0 flex items-center justify-center transition-transform duration-300 ${
                        isOpen ? 'bg-emerald-500 text-slate-950 rotate-180' : 'bg-slate-800 text-slate-400'
                      }`}
                    >
                      <ChevronDown className="w-5 h-5" />
                    </div>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                      >
                        <div className="px-5 pb-6 sm:px-6 pt-0 border-t border-slate-800/60 mt-1">
                          <p className="text-sm sm:text-base text-slate-300 leading-relaxed pt-4 font-normal">
                            {faq.answer}
                          </p>

                          {/* Tags */}
                          <div className="flex flex-wrap items-center gap-1.5 pt-4">
                            {faq.tags.map((tag) => (
                              <span
                                key={tag}
                                className="text-[11px] font-medium bg-slate-950 text-slate-400 border border-slate-800 px-2.5 py-1 rounded-lg"
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })
          )}
        </div>

        {/* Bottom Contact / Audit CTA Banner */}
        <div className="bg-gradient-to-r from-emerald-950 via-slate-900 to-teal-950 border border-emerald-500/40 rounded-3xl p-6 sm:p-8 shadow-2xl relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" /> Have A Specific Roof or Facility Question?
            </div>
            <h3 className="text-2xl font-black text-white">
              Speak directly with an Access Tech COREN Engineer
            </h3>
            <p className="text-slate-300 text-xs sm:text-sm max-w-xl">
              We conduct free physical and satellite site audits across all states in Nigeria. Get customized load calculation and a transparent installation quote with no obligation.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full md:w-auto">
            <button
              onClick={() => setIsLeadModalOpen(true, 'FAQ Custom Consultation')}
              className="px-6 py-3.5 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20"
            >
              <PhoneCall className="w-4 h-4" /> Book Technical Consultation
            </button>
            <button
              onClick={() => setIsLeadModalOpen(true, 'Free Site Audit')}
              className="px-6 py-3.5 rounded-2xl bg-slate-950 hover:bg-slate-800 border border-slate-800 text-slate-200 font-bold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2"
            >
              Request Free Site Audit <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
